import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, registerUser, sendPasswordResetEmail } from '../services/api';
import './LoginPage.css';

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPrivacyAlert, setShowPrivacyAlert] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isRegistering) {
      if (!username || !email || !password) {
        setError('Please fill in all fields.');
        return;
      }
    
      const consent = localStorage.getItem('privacyConsent');
      if (consent !== 'accepted') {
        setShowPrivacyAlert(true);
        return;
      }
    }
    

    if (!isRegistering) {
      if (!emailOrUsername || !password) {
        setError('Please enter your username/email and password.');
        return;
      }
    
      const consent = localStorage.getItem('privacyConsent');
      if (consent === 'declined') {
        setShowPrivacyAlert(true);
        return;
      }
    }
    

    setLoading(true);
    setError('');

    try {
      let response;

      if (isRegistering) {
        response = await registerUser(username, email, password);
        if (response.message === 'User registered successfully!') {
          localStorage.setItem('userId', response.userId);
          localStorage.setItem('userEmail', email);
          localStorage.setItem('username', username);
          if (typeof onLogin === 'function') onLogin(email);
        } else {
          setError(response.error || 'Registration failed.');
        }
      } else {
        response = await loginUser(emailOrUsername, password);
        if (response.message === 'Login successful!') {
          localStorage.setItem('userId', response.userId);
          localStorage.setItem('userEmail', response.email);
          localStorage.setItem('username', response.username || emailOrUsername);
          if (typeof onLogin === 'function') onLogin(response.email);
        } else {
          setError(response.error || 'Login failed.');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const identifier = emailOrUsername.trim();
    if (!identifier) return alert('Please enter your email or username first.');

    try {
      const res = await sendPasswordResetEmail(identifier);
      alert(res.message || 'Check your inbox for reset instructions.');
    } catch (err) {
      alert(err.message || 'Failed to send reset email.');
    }
  };

  const toggleMode = () => {
    setIsRegistering((prev) => !prev);
    setError('');
    setEmail('');
    setUsername('');
    setEmailOrUsername('');
    setPassword('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h2 className="login-title">{isRegistering ? 'Create Account' : 'Welcome Back'}</h2>
          <p className="login-subtitle">
            {isRegistering
              ? 'Sign up to discover and save your favorite books'
              : 'Sign in to access your bookshelf'}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          {isRegistering ? (
            <>
              <div className="form-group">
                <label htmlFor="username" className="form-label">Username</label>
                <input
                  type="text"
                  id="username"
                  className="form-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="yourname@example.com"
                  required
                />
                
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="emailOrUsername" className="form-label">Username or Email</label>
              <input
                type="text"
                id="emailOrUsername"
                className="form-input"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                placeholder="Enter your username or email"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? <span className="loading-spinner"></span> : isRegistering ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p className="toggle-text">
            {isRegistering ? 'Already have an account?' : "Don't have an account?"}
            <button onClick={toggleMode} className="toggle-link">
              {isRegistering ? ' Sign In' : ' Sign Up'}
            </button>
          </p>
          {!isRegistering && (
            <p className="forgot-password-text">
              <button onClick={handleForgotPassword} className="toggle-link">
                Forgot your password?
              </button>
            </p>
          )}
        </div>
      </div>
      {showPrivacyAlert && (
  <div className="privacy-modal">
    <div className="privacy-modal-content">
      <h3>Privacy Policy Required</h3>
      <p>
        You must accept our <a href="/privacy-policy" className="privacy-link">Privacy Policy</a> to create an account.
      </p>
      <div className="modal-buttons">
        <button
          onClick={() => {
            localStorage.setItem('privacyConsent', 'declined');
            localStorage.setItem('userMode', 'guest');
            setShowPrivacyAlert(false);
            navigate('/');
          }}
          className="guest-button"
        >
          Continue as Guest
        </button>
        <button
          onClick={() => navigate('/privacy-policy')}
          className="view-policy-button"
        >
          View Privacy Policy
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default LoginPage;
